import { reportion } from "./foodView";
import { entryFor } from "./intake";
import type { CustomFood, FoodEntry, FoodItem, KayaState, MealSlot } from "./types";

/**
 * Foods the user entered by hand, offered beside the generated table.
 *
 * They live in the synced blob rather than in `foods.json`, which is regenerated and shared by
 * everyone. The picker searches one list, so they are converted to the table's row shape here,
 * tagged `src: "c"` so a screen can mark them as the user's own.
 */

/** The live custom foods — tombstoned ids dropped — oldest first. */
export function customFoodsOf(S: KayaState): CustomFood[] {
  const off = S.customFoodsOff || {};
  return (S.customFoods || []).filter((f) => !(f.id in off)).sort((a, b) => a.at - b.at);
}

/** One custom food as a table row. Values the user left out stay absent (invariant 18). */
export function customFoodItem(f: CustomFood): FoodItem {
  const v = f.per100;
  const row: FoodItem = { i: f.id, n: f.name, u: f.unit, g: f.g, k: v.k, p: v.p, c: v.c, f: v.f, src: "c" };
  for (const k of ["fb", "sf", "na", "ca", "fe", "pot"] as const) {
    const x = v[k];
    if (typeof x === "number") row[k] = x;
  }
  if (f.dish && f.dish.potG > 0) row.dishG = f.dish.potG;
  return row;
}

/**
 * The generated table plus the user's own foods. Theirs come first, so wherever the two rank
 * equally — and an exact name always ranks theirs first (`lib/foodDb.ts::orderMatches`) — the
 * food they typed in wins over the table's.
 */
export function foodTable(S: KayaState, db: FoodItem[]): FoodItem[] {
  const mine = customFoodsOf(S).map(customFoodItem);
  return mine.length ? [...mine, ...db] : db;
}

/** The nutrient keys a custom food carries, in the order the sheet asks for them. */
export const CUSTOM_REQUIRED = ["k", "p", "c", "f"] as const;
export const CUSTOM_OPTIONAL = ["fb", "sf", "na", "ca", "fe", "pot"] as const;
export type NutrientKey = (typeof CUSTOM_REQUIRED)[number] | (typeof CUSTOM_OPTIONAL)[number];
export type NutrientValues = Partial<Record<NutrientKey, number>>;

const r1 = (n: number) => Math.round(n * 10) / 10;

/**
 * Values read off a label "per serving", restated per 100 g — the only basis the table and
 * `entryFor` know. A value that was not given stays absent (invariant 18); null when the serving
 * weight is not a positive number, because there is nothing to scale by.
 */
export function toPer100(values: NutrientValues, g: number): NutrientValues | null {
  if (!Number.isFinite(g) || g <= 0) return null;
  const out: NutrientValues = {};
  for (const [k, v] of Object.entries(values) as [NutrientKey, number | undefined][]) {
    if (typeof v === "number" && Number.isFinite(v)) out[k] = r1((v * 100) / g);
  }
  return out;
}

/** What the Add-a-food sheet holds while it is being filled in: every box as typed. */
export interface CustomFoodDraft {
  name: string;
  unit: string;
  g: string;
  /** what the typed numbers are per — a label prints both, and people copy whichever is larger */
  per: "100g" | "serving";
  values: Partial<Record<NutrientKey, string>>;
}

/** The draft for editing a saved food: its own per-100 g values, nothing inferred. */
export function draftOf(f: CustomFood): CustomFoodDraft {
  const values: Partial<Record<NutrientKey, string>> = {};
  for (const k of [...CUSTOM_REQUIRED, ...CUSTOM_OPTIONAL]) {
    const v = f.per100[k];
    if (typeof v === "number") values[k] = String(v);
  }
  return { name: f.name, unit: f.unit, g: String(f.g), per: "100g", values };
}

export const EMPTY_DRAFT: CustomFoodDraft = { name: "", unit: "", g: "", per: "100g", values: {} };

/**
 * The draft as a food ready for `addCustomFood`, or the first thing stopping it, in words for the
 * ActionBar. Blank optional boxes are left out, never written as 0 (invariant 18). The two sanity
 * limits catch the usual slip — per-serving figures entered as per 100 g, or the other way round:
 * nothing edible passes 900 kcal (pure fat) or 100 g of macronutrients in 100 g.
 */
export function readCustomFood(
  d: CustomFoodDraft
): { food: Omit<CustomFood, "id" | "at">; reason?: undefined } | { food?: undefined; reason: string } {
  const name = d.name.trim();
  if (!name) return { reason: "Name it" };
  const g = Number(d.g);
  if (!d.g.trim() || !Number.isFinite(g) || g <= 0) return { reason: "Serving weight in grams" };

  const typed: NutrientValues = {};
  for (const k of CUSTOM_REQUIRED) {
    const s = (d.values[k] ?? "").trim();
    const v = Number(s);
    if (!s || !Number.isFinite(v) || v < 0) return { reason: "kcal, protein, carbs and fat are needed" };
    typed[k] = v;
  }
  for (const k of CUSTOM_OPTIONAL) {
    const s = (d.values[k] ?? "").trim();
    if (!s) continue;
    const v = Number(s);
    if (!Number.isFinite(v) || v < 0) return { reason: "Values are numbers, 0 or more" };
    typed[k] = v;
  }

  const per = d.per === "serving" ? toPer100(typed, g) : typed;
  if (!per) return { reason: "Serving weight in grams" };
  const { k, p, c, f } = per;
  if (k === undefined || p === undefined || c === undefined || f === undefined) {
    return { reason: "kcal, protein, carbs and fat are needed" };
  }
  if (k > 900) return { reason: "Over 900 kcal per 100 g — check the values" };
  if (p + c + f > 100) return { reason: "Over 100 g of protein, carbs and fat per 100 g — check the values" };

  const per100: CustomFood["per100"] = { k, p, c, f };
  for (const key of CUSTOM_OPTIONAL) {
    const v = per[key];
    if (typeof v === "number") per100[key] = v;
  }
  return { food: { name: name.slice(0, 80), unit: d.unit.trim() || "serving", g: r1(g), per100 } };
}

/** kcal in one serving of a saved food, for the rows that list them. */
export function servingKcal(f: CustomFood): number {
  return Math.round((f.per100.k * f.g) / 100);
}

/** A live custom food by its code, as a table row; null for a table code or one since removed. */
export function customFoodByCode(S: KayaState, code: string): FoodItem | null {
  if (!code.startsWith("cf:")) return null;
  const f = customFoodsOf(S).find((x) => x.id === code);
  return f ? customFoodItem(f) : null;
}

/**
 * The entry "log again" writes. From the row when it still exists — for a food of the user's own
 * that is its current version. A custom food edited or deleted since keeps no row under the old
 * code, so the entry is copied from its own frozen values instead (ADR-032 item 6); a table code
 * that is gone gives null, as it always has.
 */
export function againEntry(
  prev: FoodEntry,
  item: FoodItem | null,
  opts: { meal: MealSlot; at?: number }
): FoodEntry | null {
  const at = opts.at ?? Date.now();
  if (item) return entryFor(item, { qty: prev.qty, unit: prev.unit, meal: opts.meal, oilTsp: prev.oilTsp, at });
  if (!prev.code?.startsWith("cf:")) return null;
  const id = `${at.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const copy = reportion(prev, { qty: prev.qty, oilTsp: prev.oilTsp || 0, id, at });
  return copy ? { ...copy, meal: opts.meal } : null;
}
